import type { TenantId } from '../domain/entities/tenant-ids';
import type { GabaritoSubmissionState } from '../domain/entities/tenant.entity';

/**
 * Gera ids de exemplo (`mock-amrigs-01`…). Existem só para o ensaio rodar
 * sobre o mock enquanto os ids reais de uma prova não chegam — o mock aceita
 * qualquer id. Contra a API real eles não devolvem nada.
 */
function mockIds(tenant: TenantId, count: number): string[] {
  return Array.from(
    { length: count },
    (_, index) => `mock-${tenant}-${String(index + 1).padStart(2, '0')}`,
  );
}

/** O que muda de uma edição para outra da mesma prova. */
export interface ExamEditionConfig {
  /**
   * Quantas questões a prova oficial tem — o tamanho da folha do gabarito.
   * **A confirmar por prova**: hoje todas estão com 100.
   */
  totalQuestions: number;
  /** 4 (A–D) ou 5 (A–E). As provas que têm a quinta alternativa usam 5. */
  optionCount: 4 | 5;
  /**
   * Os `questionId` da Aristo do ensaio, na ordem dele: é esta lista que vai no
   * corpo da busca das questões, e o número na tela é a posição nela.
   *
   * **Não reordene com a edição no ar.** O rascunho guarda as respostas pelo
   * número da questão; trocar a ordem faria a resposta de quem já começou cair
   * em outra questão. Acrescentar no fim é seguro.
   */
  ensaioQuestionIds: readonly string[];
  /**
   * Se o envio do gabarito está aberto — o botão "Enviar meu gabarito" da home,
   * a página `/gabarito` e os convites do painel. Muda com deploy:
   *
   * - `'soon'` antes da prova (o botão aparece como "em breve");
   * - `'open'` a partir do dia da prova;
   * - `'closed'` quando o envio encerra (o botão some; quem já enviou continua
   *   vendo a correção no painel).
   */
  gabaritoSubmission: GabaritoSubmissionState;
  /**
   * Link **público** do arquivo da prova do ensaio (PDF), para quem prefere
   * resolver no papel. Com ele, a página do ensaio mostra "Baixar o ensaio"
   * (abre em aba nova); quem resolve no papel depois usa "Fornecer respostas"
   * para marcar tudo de uma vez. `https://` obrigatório (o teste do mapper
   * confere).
   */
  ensaioDownloadUrl?: string;
  /**
   * Link da página de download do PDF das **Previsões Aristo** da prova
   * (`https://`, aba nova). Com ele, a página do ensaio mostra o botão de
   * destaque "Previsões Aristo"; sem ele, o botão não aparece. Por prova e
   * ano, como o resto daqui.
   */
  previsoesAristoUrl?: string;
  /**
   * Números das questões da prova oficial **passíveis de recurso**, na leitura
   * da Aristo. Só sinalizam na grade da aba Gabarito; não mudam a nota.
   */
  recursoQuestions?: readonly number[];
}

/**
 * **Cada prova, cada ano.** Qual edição um build serve não mora aqui: vem de
 * `VITE_EDITION_<PROVA>` (ver `getTenant`), para que trocar de ano seja uma
 * linha no `.env.prod` e um deploy, sem mexer em código. Edição nova é uma
 * chave nova aqui; as antigas ficam, para histórico e para voltar atrás.
 *
 * Hoje a HCPA 2026 e a AMRIGS 2026 têm os ids reais do ensaio.
 */
export const EXAM_EDITIONS: Record<
  TenantId,
  Record<number, ExamEditionConfig>
> = {
  amrigs: {
    2026: {
      ensaioDownloadUrl: 'https://google.com.br',
      totalQuestions: 100,
      optionCount: 5,
      gabaritoSubmission: 'closed',
      ensaioQuestionIds: [
        '659e91d464630851e618c947',
        '67647fbd750d7b9a16eca4a9',
        '67647fbd750d7b9a16eca4bd',
        '67647fbd750d7b9a16eca4a6',
        '659e91d464630851e618c920',
        '659e91d464630851e618c92a',
        '67647fbd750d7b9a16eca474',
        '69a72864c39c78330e82c8f2',
        '69a72864c39c78330e82c8f4',
        '659e91d464630851e618c914',
        '67647fbd750d7b9a16eca471',
        '67647fbd750d7b9a16eca479',
        '6385ef9098ee38616334b383',
        '6385ef9098ee38616334b388',
        '69a72866c39c78330e82c91c',
        '6385ef9098ee38616334b379',
        '69a72867c39c78330e82c930',
        '6385ef9098ee38616334b37a',
        '67647fbd750d7b9a16eca4a3',
        '69a72869c39c78330e82c95c',
        '659e91d464630851e618c92e',
        '69a72868c39c78330e82c944',
        '67647fbd750d7b9a16eca4a8',
        '5f5fa62a8c17db9f3a8dfb61',
        '69a72869c39c78330e82c962',
        '69a72867c39c78330e82c938',
        '67647fbd750d7b9a16eca4ab',
        '67647fbd750d7b9a16eca4b4',
        '659e91d464630851e618c93f',
        '69a72869c39c78330e82c960',
      ],
    },
  },
  'psu-mg': {
    2026: {
      ensaioDownloadUrl: 'https://google.com.br',
      totalQuestions: 100,
      optionCount: 5,
      gabaritoSubmission: 'open',
      ensaioQuestionIds: mockIds('psu-mg', 20),
    },
  },
  scmbh: {
    2026: {
      ensaioDownloadUrl: 'https://google.com.br',
      totalQuestions: 100,
      optionCount: 5,
      gabaritoSubmission: 'open',
      ensaioQuestionIds: mockIds('scmbh', 20),
    },
  },
  hcpa: {
    2026: {
      ensaioDownloadUrl: 'https://google.com.br',
      previsoesAristoUrl: 'https://google.com.br',
      totalQuestions: 100,
      optionCount: 4,
      gabaritoSubmission: 'open',
      recursoQuestions: [78, 99],
      ensaioQuestionIds: [
        '695d4064690ce3bd6247d28e',
        '695d4064690ce3bd6247d290',
        '695d4064690ce3bd6247d273',
        '6764804b836824ed77f6a9ec',
        '695d4064690ce3bd6247d286',
        '6764804b836824ed77f6aa15',
        '6764804b836824ed77f6a9e1',
        '695d4064690ce3bd6247d27a',
        '6764804b836824ed77f6a9f0',
        '695d4064690ce3bd6247d291',
        '695d4064690ce3bd6247d280',
        '6764804b836824ed77f6a9f2',
        '6764804b836824ed77f6a9cf',
        '628dbff57b850b0d028ab5db',
        '637249139392f81484be8d9a',
        '637249139392f81484be8d8d',
        '6560fe310c6ed9cf85e489b9',
        '695d4064690ce3bd6247d26a',
        '628dbff57b850b0d028ab5c7',
        '6560fe300c6ed9cf85e489a1',
        '695d4064690ce3bd6247d257',
        '637249139392f81484be8d86',
        '695d4064690ce3bd6247d249',
        '6560fe300c6ed9cf85e489a7',
        '6764804b836824ed77f6aa0e',
        '6764804b836824ed77f6aa11',
        '6560fe310c6ed9cf85e489f3',
        '695d4064690ce3bd6247d298',
        '6764804b836824ed77f6aa0b',
        '637249139392f81484be8dca',
      ],
    },
  },
};
